import { Box, Button, Group, Modal, Stack, Text, Textarea, Tooltip } from '@mantine/core';
import { useState } from 'react';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import FileUpload from '../FileUpload';
import DomainSecretSelect from '../Settings/DomainSecretSelect';
import SecretCreateModal from '../Settings/SecretCreateModal';

export const parseBundle = (text) => {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: 'That is not valid JSON.' };
  }

  if (parsed?.kind !== 'releva.connector-bundle') return { error: "Not a connector bundle: kind must be 'releva.connector-bundle'." };
  if (parsed?.version !== 1) return { error: 'This bundle is a version the admin does not support.' };
  if (!Array.isArray(parsed.instances) || parsed.instances.length === 0) return { error: 'The bundle has no instances.' };

  const slots = [...new Set(parsed.instances.map((instance) => instance.secret?.slot).filter(Boolean))];

  return { bundle: parsed, slots };
};

const resultLine = (result) => (result.ok ? 'created as draft' : result.message || result.code || 'failed');

const BundleImportModal = ({ opened, onClose, apiBase, accountId, domainId }) => {
  const fetch = useFetch();

  const [text, setText] = useState('');
  const [secretIds, setSecretIds] = useState({});
  const [secretModalSlot, setSecretModalSlot] = useState(null);
  const [secretKey, setSecretKey] = useState(0);
  const [secretError, setSecretError] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [results, setResults] = useState(null);

  const parsed = text.trim() ? parseBundle(text) : null;
  const slots = parsed?.slots || [];
  const unmapped = slots.filter((slot) => secretIds[slot] == null);
  const canImport = !!parsed?.bundle && unmapped.length === 0;

  const readFile = ([file]) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => setText(event.target.result);
    reader.readAsText(file);
  };

  const close = () => {
    setText('');
    setSecretIds({});
    setResults(null);
    setImportError(null);
    // A /secrets read that failed once would otherwise sit in the dialog on every later open,
    // under a bundle it has nothing to do with.
    setSecretError(null);
    onClose();
  };

  const runImport = () => {
    setImporting(true);
    setImportError(null);

    const secrets = Object.fromEntries(slots.map((slot) => [slot, secretIds[slot]]));

    fetch(`${apiBase}/bundle`, { method: 'POST', body: JSON.stringify({ bundle: parsed.bundle, secrets }) })
      .then((res) => setResults(res.results || []))
      .catch((err) => setImportError(getErrorMessage(err, 'The bundle could not be imported.')))
      .finally(() => setImporting(false));
  };

  const importButton = (
    <Button loading={importing} disabled={!canImport} onClick={runImport}>
      Import
    </Button>
  );

  return (
    <Modal opened={opened} onClose={close} title="Import bundle" size="lg" centered>
      <Stack gap="sm">
        <Textarea
          label="Bundle JSON"
          placeholder="Paste the bundle here"
          autosize
          minRows={6}
          maxRows={16}
          styles={{ input: { fontFamily: 'monospace', fontSize: 12 } }}
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <FileUpload label="Or upload a bundle file" onFileSet={readFile} />

        {parsed?.error && <ErrorMessage message={parsed.error} />}

        {slots.map((slot) => (
          <Group key={slot} align="flex-end" gap="xs" wrap="nowrap">
            <DomainSecretSelect
              label={slot}
              description="The secret this slot is mapped to for this store."
              accountId={accountId}
              domainId={domainId}
              refreshKey={secretKey}
              required
              value={secretIds[slot] ?? null}
              onChange={(value) => setSecretIds((previous) => ({ ...previous, [slot]: value }))}
              onError={(err) => setSecretError(getErrorMessage(err, 'Could not load the secrets of this store.'))}
            />
            <Button variant="subtle" onClick={() => setSecretModalSlot(slot)}>
              New secret
            </Button>
          </Group>
        ))}

        {secretError && <ErrorMessage message={secretError} />}
        {importError && <ErrorMessage message={importError} />}

        {results && (
          <Stack gap={2}>
            {results.map((result) => (
              <Text key={result.index} size="sm">
                {result.name}: {resultLine(result)}
              </Text>
            ))}
          </Stack>
        )}

        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={close}>
            Close
          </Button>
          {unmapped.length ? (
            <Tooltip label={`Map a secret for ${unmapped.join(', ')} first`} withArrow>
              <Box>{importButton}</Box>
            </Tooltip>
          ) : (
            importButton
          )}
        </Group>
      </Stack>

      <SecretCreateModal
        opened={secretModalSlot !== null}
        onClose={() => setSecretModalSlot(null)}
        onCreated={(secret) => {
          setSecretIds((previous) => ({ ...previous, [secretModalSlot]: secret.id }));
          setSecretKey((key) => key + 1);
        }}
        baseUrl={`/api/admin/accounts/${accountId}/domains/${domainId}/secrets`}
      />
    </Modal>
  );
};

export default BundleImportModal;
